import React, { useEffect, useState } from "react";
import { MapPin, Calendar, Users } from "lucide-react";
import { api, buildWaLink } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import Reveal from "./Reveal";
import PackageDetailModal from "./PackageDetailModal";

const getPromoLabel = (title) => {
  if (title?.includes("Golden")) return "Golden Promo";
  if (title?.includes("Leisure")) return "Leisure Escape";
  if (title?.includes("Short")) return "Short Escape";
  return "Tour Package";
};

const formatIDR = (n) => `IDR ${Number(n).toLocaleString("id-ID")}`;

export const PaketPopuler = () => {
  const { t, lang } = useLang();
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get(`/packages?lang=${lang}`)
      .then((r) => {
        if (mounted) setPackages(Array.isArray(r.data) ? r.data : []);
      })
      .catch((err) => console.error("Failed to load packages", err));
    return () => {
      mounted = false;
    };
  }, [lang]);

  return (
    <section id="paket" data-testid="paket-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="grid items-end gap-8 md:grid-cols-12 md:gap-12">
          <Reveal className="md:col-span-7">
            <span className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#1E7FC2]">
              <span className="h-px w-8 bg-[#1E7FC2]" />
              {t.paket.eyebrow}
            </span>
            <h2 className="mt-5 font-[Fraunces] text-4xl font-medium leading-[1.05] tracking-tight text-[#0C2230] sm:text-5xl">
              {t.paket.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <p className="text-base leading-relaxed text-[#0C2230]/70 md:text-lg">{t.paket.sub}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((p, idx) => {
            const durationMatch = (p.duration || "").match(/(\d+)/);
            const days = durationMatch ? `${durationMatch[1]} ${durationMatch[1] === "1" ? "Day" : "Days"}` : p.duration;
            const fromCountry = p.departure_from || p.subtitle?.replace(/From /i, "") || "";
            const promoLabel = getPromoLabel(p.title);

            return (
              <Reveal key={p.id} delay={idx * 0.06}>
                <article
                  data-testid={`paket-card-${idx}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_8px_24px_-10px_rgba(12,34,48,0.18)] ring-1 ring-[#0C2230]/8 transition-all duration-500 hover:shadow-[0_20px_48px_-15px_rgba(12,34,48,0.3)] hover:-translate-y-1"
                >
                  {/* Image with promo badge + overlay text */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                      loading="lazy"
                    />
                    {p.discount_percent ? (
                      <div className="absolute -right-10 top-5 z-10 w-40 rotate-45 bg-gradient-to-r from-[#0E9A57] to-[#13b86a] py-1.5 text-center text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
                        {p.discount_percent}% Off
                      </div>
                    ) : null}

                    <div className="absolute left-5 top-5 z-[5] flex flex-col gap-1.5 drop-shadow-[0_3px_6px_rgba(0,0,0,0.4)]">
                      <span className="font-[Fraunces] text-2xl font-bold leading-none text-white md:text-3xl">
                        {p.duration?.toUpperCase()} BATAM
                      </span>
                      <span className="font-[Fraunces] text-xl font-medium leading-none text-white md:text-2xl">
                        TOUR
                      </span>
                      <span className="mt-2 inline-block rounded-md bg-[#0C2230] px-3 py-1 text-xs font-semibold text-white w-fit">
                        {promoLabel}
                      </span>
                      {fromCountry && (
                        <span className="inline-block rounded-md bg-[#FBBF24] px-3 py-1 text-xs font-bold text-[#0C2230] w-fit">
                          Start from <span className="text-[#DC2626]">{fromCountry}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="flex flex-1 flex-col gap-4 p-6">
                    <div className="flex items-center gap-1.5 text-xs text-[#0C2230]/65">
                      <MapPin className="h-3.5 w-3.5 text-[#1E7FC2]" />
                      <span>Indonesia, Batam Island</span>
                    </div>

                    <h3 className="font-[Fraunces] text-xl font-medium leading-snug text-[#0C2230] min-h-[3.5rem]">
                      {p.title} {p.subtitle ? `– ${p.subtitle}` : ""}
                    </h3>

                    <div className="flex gap-3">
                      <div className="flex flex-1 items-center gap-2 rounded-lg bg-[#FBFAF6] px-3 py-2">
                        <Calendar className="h-4 w-4 text-[#0C2230]/60" />
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#0C2230]/55">Duration</p>
                          <p className="text-sm font-semibold text-[#0C2230]">{days}</p>
                        </div>
                      </div>
                      <div className="flex flex-1 items-center gap-2 rounded-lg bg-[#FBFAF6] px-3 py-2">
                        <Users className="h-4 w-4 text-[#0C2230]/60" />
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#0C2230]/55">Group</p>
                          <p className="text-sm font-semibold text-[#0C2230]">Max 27</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto flex items-end justify-between gap-3 border-t border-[#0C2230]/10 pt-4">
                      <div>
                        {p.price_original && p.price_original > p.price_from ? (
                          <p className="text-xs text-[#DC2626] line-through">
                            {t.paket.from} {formatIDR(p.price_original)}
                          </p>
                        ) : (
                          <p className="text-xs text-[#0C2230]/55">{t.paket.from}</p>
                        )}
                        <p className="font-[Fraunces] text-2xl font-bold text-[#0C2230]">
                          {formatIDR(p.price_from)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedPkg(p)}
                        data-testid={`paket-view-${idx}`}
                        className="inline-flex items-center justify-center rounded-lg bg-[#0E9A57] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#0c8a4d] hover:shadow-lg"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
      {selectedPkg && (
        <PackageDetailModal pkg={selectedPkg} onClose={() => setSelectedPkg(null)} />
      )}
    </section>
  );
};

export default PaketPopuler;
